import { useEffect, useRef, useState } from 'react';

/**
 * CS-3 Phase 12 — one reusable tenant-branded welcome transition.
 *
 * Deliberately dumb/presentational: it never touches auth. Callers pass the
 * already-resolved tenant identity + audience copy and an `onContinue` handler
 * that performs the real navigation to the correct existing destination.
 *
 * - tenant logo / name / brand colour
 * - first name where available (falls back gracefully)
 * - audience/role label
 * - auto-advances in ~0.8–1.5s but Continue is available immediately
 * - accessible (role="status" + aria-live) and mobile-first
 */
const safeColour = (value) => (/^#[0-9a-fA-F]{6}$/.test(value || '') ? value : '#0F766E');

export default function TenantWelcomeSplash({
  tenantName = '',
  logoUrl = '',
  primaryColour = '#0F766E',
  firstName = '',
  audienceLabel = '',
  greeting = 'Welcome back',
  message = '',
  subMessage = 'Taking you to your next step…',
  continueLabel = 'Continue',
  autoAdvanceMs = 1200,
  onContinue,
}) {
  const colour = safeColour(primaryColour);
  const advanced = useRef(false);
  const [initial] = useState(() => (firstName || '').trim());

  const go = () => {
    if (advanced.current) return;
    advanced.current = true;
    if (typeof onContinue === 'function') onContinue();
  };

  useEffect(() => {
    // Clamp to the CS-3 window so navigation is never unnecessarily delayed.
    const ms = Math.min(Math.max(Number(autoAdvanceMs) || 1200, 800), 1500);
    const timer = setTimeout(go, ms);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const heading = initial ? `${greeting}, ${initial}` : greeting;
  const badge = (initial ? initial[0] : (tenantName || '?')[0] || '?').toUpperCase();

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: `linear-gradient(135deg, ${colour} 0%, #0f172a 150%)` }}
      data-testid="tenant-welcome-splash"
    >
      <div
        role="status"
        aria-live="polite"
        className="w-full max-w-sm rounded-3xl bg-white/95 px-8 py-10 text-center shadow-2xl backdrop-blur"
      >
        <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center overflow-hidden rounded-2xl bg-white ring-1 ring-slate-200">
          {logoUrl ? (
            <img src={logoUrl} alt={tenantName || 'Provider logo'} className="max-h-14 max-w-16 object-contain" />
          ) : (
            <span className="text-2xl font-bold" style={{ color: colour }}>{badge}</span>
          )}
        </div>

        <h1 className="font-heading text-2xl font-bold text-slate-900">{heading}</h1>
        {tenantName ? (
          <p className="mt-1 text-sm font-semibold" style={{ color: colour }}>{tenantName}</p>
        ) : null}
        {audienceLabel ? (
          <p className="mt-1 text-xs font-bold uppercase tracking-[0.22em] text-slate-400">{audienceLabel}</p>
        ) : null}
        {message ? <p className="mt-4 text-sm leading-relaxed text-slate-600">{message}</p> : null}
        {subMessage ? <p className="mt-1 text-xs text-slate-400">{subMessage}</p> : null}

        <button
          type="button"
          onClick={go}
          data-testid="tenant-welcome-continue"
          className="mt-6 inline-flex w-full items-center justify-center rounded-full px-5 py-3 text-sm font-semibold text-white transition hover:opacity-90"
          style={{ backgroundColor: colour }}
        >
          {continueLabel}
        </button>
      </div>
    </div>
  );
}
